import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";

// Thin redirect into the new OpenReel-based editor that lives under
// /editor/ (built from clipbait-master/editor/ → client/public/editor/).
// The new editor reads jobId + clipIndex from the URL query and
// auto-loads the clip via the Clipbait API. Auth flows through
// localStorage (same-origin) — no token-in-URL needed.
//
// We use a full-page navigation (window.location.replace) instead of
// react-router's navigate() because /editor/ is a separate Vite
// bundle, not part of the CRA SPA. CRA's catch-all would otherwise
// hijack the route.
//
// IMPORTANT: this also handles the "editor build is missing in
// production" case. If Render's build command hasn't been updated to
// run `editor:build`, /editor/index.html resolves to CRA's index.html
// (via the catch-all) instead of the real editor HTML. Without the
// preflight check below, the user would just see a white screen
// because React has no route for /editor/?....
//
// The preflight HEADs /editor/index.html and inspects the response —
// if it doesn't look like the editor's HTML, we show a real error
// pointing at the deploy config instead of pretending nothing's wrong.

const EDITOR_MARKER = "/editor/assets/"; // the editor's index.html
                                          // preloads chunks under this
                                          // path; CRA's never will

export default function ClipEditor() {
  const { jobId, clipIndex } = useParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/editor/index.html", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const html = await res.text();
        if (!html.includes(EDITOR_MARKER)) {
          throw new Error("Editor static bundle isn't deployed at /editor/. Check Render's build command runs `npm run editor:install && npm run editor:build`.");
        }
        if (cancelled) return;
        const safeJob = encodeURIComponent(jobId || "");
        const safeIdx = encodeURIComponent(clipIndex || "0");
        window.location.replace(`/editor/?jobId=${safeJob}&clipIndex=${safeIdx}`);
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    })();
    return () => { cancelled = true; };
  }, [jobId, clipIndex]);

  const baseStyle = {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#0a0a0a",
    color: "#fafafa",
    fontFamily: "system-ui, -apple-system, sans-serif",
    padding: 24,
    boxSizing: "border-box",
  };

  if (error) {
    return (
      <div style={baseStyle}>
        <div style={{ maxWidth: 560, textAlign: "center" }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: "#ff8a3d", marginBottom: 10 }}>
            Editor isn't deployed yet
          </div>
          <div style={{ fontSize: 13.5, color: "#a1a1aa", lineHeight: 1.55, marginBottom: 16 }}>
            {error}
          </div>
          <Link
            to="/home"
            style={{
              display: "inline-block",
              padding: "10px 20px",
              background: "#ff6a00",
              color: "#fff",
              borderRadius: 10,
              textDecoration: "none",
              fontWeight: 700,
              fontSize: 13,
            }}
          >
            Back to clips
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...baseStyle, fontSize: 14 }}>Loading editor…</div>
  );
}
